import { useCallback, useEffect, useMemo, useState } from "react";

import classes from "./UserAdmin.module.scss";
import { fetchAdminFolders, saveAdminFolders } from "./ApiService";

const folderKey = (folder) => folder.id || folder.name;

const draftFromFolder = (folder) => ({
  alias: folder.alias || "",
  priority: folder.priority == null ? "" : String(folder.priority),
});

const parsePriority = (value, folderName) => {
  const text = String(value ?? "").trim();
  if (!text) return { priority: null };
  const number = Number(text);
  if (!Number.isInteger(number)) {
    return { error: `Priority for “${folderName}” must be a whole number.` };
  }
  if (number < 1 || number > 9999) {
    return { error: `Priority for “${folderName}” must be between 1 and 9999.` };
  }
  return { priority: number };
};

const FolderAdmin = () => {
  const [folders, setFolders] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadFolders = useCallback(async ({ refresh } = {}) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminFolders({ refresh });
      const items = Array.isArray(data.folders) ? data.folders : [];
      setFolders(items);
      const nextDrafts = {};
      items.forEach((item) => {
        nextDrafts[folderKey(item)] = draftFromFolder(item);
      });
      setDrafts(nextDrafts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const sortedFolders = useMemo(() => {
    const rank = (folder) =>
      folder.priority == null ? Number.POSITIVE_INFINITY : Number(folder.priority);
    return [...folders].sort((a, b) => {
      const delta = rank(a) - rank(b);
      if (delta) return delta;
      return a.name.localeCompare(b.name);
    });
  }, [folders]);

  const aliasCount = useMemo(
    () =>
      Object.values(drafts).filter((draft) => String(draft?.alias || "").trim())
        .length,
    [drafts]
  );

  const setDraftField = (key, field, value) => {
    setDrafts((current) => ({
      ...current,
      [key]: { alias: "", priority: "", ...current[key], [field]: value },
    }));
    setNotice("");
  };

  const saveFolders = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = [];
      for (const folder of folders) {
        const draft = drafts[folderKey(folder)] || {};
        const parsed = parsePriority(draft.priority, folder.name);
        if (parsed.error) {
          setError(parsed.error);
          setSaving(false);
          return;
        }
        payload.push({
          id: folder.id,
          name: folder.name,
          alias: (draft.alias || "").trim(),
          priority: parsed.priority,
        });
      }
      const result = await saveAdminFolders(payload);
      const saved = Array.isArray(result.folders) ? result.folders : payload;
      const savedByKey = {};
      saved.forEach((item) => {
        savedByKey[folderKey(item)] = {
          alias: item.alias || "",
          priority: item.priority == null ? "" : String(item.priority),
        };
      });
      setFolders((current) =>
        current.map((folder) => ({
          ...folder,
          alias: savedByKey[folderKey(folder)]?.alias || "",
          priority: savedByKey[folderKey(folder)]
            ? parsePriority(savedByKey[folderKey(folder)].priority, folder.name)
                .priority
            : null,
        }))
      );
      setDrafts((current) => {
        const next = { ...current };
        folders.forEach((folder) => {
          next[folderKey(folder)] = savedByKey[folderKey(folder)] || {
            alias: "",
            priority: "",
          };
        });
        return next;
      });
      try {
        const aliases = {};
        const priorities = {};
        saved.forEach((item) => {
          if (item.alias && item.id) aliases[item.id] = item.alias;
          if (item.alias && item.name) aliases[item.name] = item.alias;
          if (item.priority != null && item.id) priorities[item.id] = item.priority;
          if (item.priority != null && item.name) {
            priorities[item.name] = item.priority;
          }
        });
        localStorage.setItem("folder-aliases", JSON.stringify(aliases));
        localStorage.setItem("folder-priorities", JSON.stringify(priorities));
      } catch (err) {
        // Ignore storage errors; login/refresh will reload aliases.
      }
      const count = saved.filter((item) => item.alias).length;
      setNotice(
        count
          ? `Saved ${count} folder alias${count === 1 ? "" : "es"}. Users see the new labels and order after their next sign-in.`
          : "Saved folder settings. Toolbar labels will use the default short names."
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <h1 className={classes.title}>Folder Aliases</h1>
          <p className={classes.subtitle}>
            Assign display names and toolbar order for Tableau folders that start
            with CDAnalytics. Lower priority numbers appear first when a user
            has more than one client. Leave alias blank to keep the default
            short name (the text after “CDAnalytics - ”). Leave priority blank
            to sort alphabetically after numbered folders.
          </p>
        </div>
        <div className={classes.headerActions}>
          <button
            type="button"
            className={classes.secondaryBtn}
            onClick={() => loadFolders({ refresh: true })}
          >
            Refresh
          </button>
          <button
            type="button"
            className={classes.primaryBtn}
            onClick={saveFolders}
            disabled={saving || loading}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <div className={classes.error}>{error}</div>}
      {notice && <div className={classes.notice}>{notice}</div>}

      <div className={classes.tableWrapper}>
        {loading ? (
          <div className={classes.placeholder}>Loading Tableau folders…</div>
        ) : sortedFolders.length === 0 ? (
          <div className={classes.placeholder}>
            No Tableau folders found with a CDAnalytics prefix.
          </div>
        ) : (
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Tableau folder</th>
                <th>Default label</th>
                <th>Alias</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {sortedFolders.map((folder) => {
                const key = folderKey(folder);
                const draft = drafts[key] || { alias: "", priority: "" };
                return (
                  <tr key={key}>
                    <td>
                      {folder.name}
                    </td>
                    <td className={classes.muted}>{folder.default_label}</td>
                    <td>
                      <input
                        className={classes.aliasInput}
                        type="text"
                        maxLength={80}
                        value={draft.alias}
                        placeholder={folder.default_label}
                        onChange={(event) =>
                          setDraftField(key, "alias", event.target.value)
                        }
                        aria-label={`Alias for ${folder.name}`}
                      />
                    </td>
                    <td>
                      <input
                        className={classes.priorityInput}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        value={draft.priority}
                        placeholder="—"
                        onChange={(event) =>
                          setDraftField(key, "priority", event.target.value)
                        }
                        aria-label={`Priority for ${folder.name}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!loading && sortedFolders.length > 0 && (
        <p className={classes.muted} style={{ margin: 0 }}>
          {aliasCount} alias{aliasCount === 1 ? "" : "es"} set
        </p>
      )}
    </div>
  );
};

export default FolderAdmin;
